import React, { useRef, useState } from 'react';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import rootCss from './autocomplete.module.scss';
import css from './option.module.scss';
import { type BaseValue } from './types';

type Props<Option extends BaseValue> = {
  option: Option;
  onUpdate?: (option: Option, nextOption: Option) => void;
  onRemove?: (option: Option) => void;
  isEditing: boolean;
  onEditStart(): void;
  onEditEnd(): void;
};

function Option<Option extends BaseValue>(props: Props<Option>) {
  const { option, onUpdate, onRemove, isEditing, onEditStart, onEditEnd } =
    props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(option.label);

  const startEdit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    onEditStart();
  };

  const cancelEdit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    onEditEnd();
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    onUpdate?.(option, { label: inputRef.current?.value } as Option);
    onEditEnd();
  };

  const handleOptionRemove = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    onRemove?.(option);
  };

  const handleChange = (e: any) => {
    const input = e.target.value;
    const alphanumeric = input.replace(/[^a-zA-Z0-9 ]/g, '');
    setInputValue(alphanumeric);
  };

  return (
    <div className={css.root}>
      <Icon name={'far fa-square'} className={rootCss['check-icon']} />
      {isEditing ? (
        <form onSubmit={handleSubmit}>
          <input
            className={css.input}
            ref={inputRef}
            type="text"
            autoFocus
            value={inputValue}
            onChange={handleChange}
            onKeyDown={(event) => {
              if (event.code === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onEditEnd();
              }
            }}
          />
          <Button size="xxs" type="submit" variant="icon">
            <Icon name={'far fa-check'} className={css.icon} />
          </Button>
          <Button size="xxs" variant="icon" onClick={cancelEdit}>
            <Icon name={'far fa-times'} className={css.icon} />
          </Button>
        </form>
      ) : (
        <>
          <span className={css.label}>{option.label}</span>
          {onUpdate && (
            <Button
              className={css.action}
              size="xxs"
              variant="icon"
              onClick={startEdit}
            >
              <Icon name={'far fa-pencil'} className={css.icon} />
            </Button>
          )}
          {onRemove && (
            <Button
              className={css.action}
              size="xxs"
              variant="icon"
              onClick={(event: any) => handleOptionRemove(event)}
            >
              <Icon name={'far fa-trash'} className={css.icon} />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default Option;
